import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Package, Plus, Edit2, Trash2, Archive, ChevronRight,
  X, MoreVertical, Check, ArchiveRestore, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../UI';
import { useGroceryLists } from '../../hooks/useGroceryLists';
import { usePantries } from '../../hooks/usePantries';
import { useGuestList } from '../../hooks/useGuestList';
import { useAuth } from '../../hooks/useAuth';

const safeString = (val) => {
  if (typeof val === 'string') return val;
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return val.name || val.text || val.value || '';
  return String(val);
};

const normalizeList = (list) => ({
  id: list.id,
  name: safeString(list.name) || 'Unnamed List',
  items: Array.isArray(list.items) ? list.items : [],
  updatedAt: list.updatedAt || null,
});

const normalizePantry = (pantry) => ({
  id: pantry.id,
  name: safeString(pantry.name) || 'Unnamed Pantry',
  description: safeString(pantry.description),
  productIds: Array.isArray(pantry.productIds) ? pantry.productIds : [],
});

// Individual item row — product image + name + checkbox
const ListItem = ({ item, onToggle, showToggle = true }) => {
  const name = safeString(item.name);
  const isChecked = item.isChecked || false;

  return (
    <div className={`flex items-center gap-sm p-sm rounded-medium transition-all ${isChecked ? 'opacity-50' : 'hover:bg-primary/5'}`}>
      {/* Product image or placeholder */}
      <div className="w-9 h-9 rounded-medium bg-gradient-to-br from-accent/10 to-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden border border-text-tertiary/10">
        {item.productImageURL ? (
          <img src={item.productImageURL} alt={name} className="w-full h-full object-contain p-xs" />
        ) : (
          <Package size={14} className="text-text-tertiary" />
        )}
      </div>

      {/* Name + qty */}
      <div className="flex-1 min-w-0">
        <p className={`text-caption font-semibold truncate ${isChecked ? 'line-through text-text-tertiary' : 'text-text-primary'}`}>
          {name}
        </p>
        {item.quantity > 1 && (
          <p className="text-footnote text-text-tertiary">Qty: {item.quantity}</p>
        )}
      </div>

      {/* Checkbox */}
      {showToggle && onToggle && (
        <button
          onClick={() => onToggle(item.id)}
          className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
            isChecked
              ? 'bg-success border-success'
              : 'border-text-tertiary/30 hover:border-primary'
          }`}
        >
          {isChecked && <Check size={12} className="text-white" />}
        </button>
      )}
    </div>
  );
};

const ProductsSidebar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('lists');

  const {
    groceryLists: rawGroceryLists = [],
    archivedLists: rawArchivedLists = [],
    createList, renameList, deleteList, archiveList, unarchiveList, toggleItem
  } = useGroceryLists();

  const { guestList, updateItem: updateGuestItem } = useGuestList();
  const { pantries: rawPantries = [], createPantry, renamePantry, deletePantry } = usePantries();

  const groceryLists = rawGroceryLists.map(normalizeList);
  const archivedLists = rawArchivedLists.map(normalizeList);
  const pantries = rawPantries.map(normalizePantry);
  const guestItems = guestList?.items || [];

  const [activeListId, setActiveListId] = useState(null);
  const [activePantryId, setActivePantryId] = useState(null);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [showCreatePantryModal, setShowCreatePantryModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [expandedArchivedId, setExpandedArchivedId] = useState(null);

  const [listName, setListName] = useState('');
  const [pantryName, setPantryName] = useState('');
  const [pantryDescription, setPantryDescription] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  React.useEffect(() => {
    if (user && groceryLists.length > 0 && !activeListId) {
      setActiveListId(groceryLists[0].id);
    }
  }, [groceryLists, activeListId, user]);

  React.useEffect(() => {
    if (user && pantries.length > 0 && !activePantryId) {
      setActivePantryId(pantries[0].id);
    }
  }, [pantries, activePantryId, user]);

  const activeList = user ? groceryLists.find(l => l.id === activeListId) : null;
  const activePantry = pantries.find(p => p.id === activePantryId);

  const checkedCount = activeList?.items.filter(i => i.isChecked).length || 0;
  const totalCount = activeList?.items.length || 0;
  const progress = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;

  const handleToggleItem = async (itemId) => {
    if (!activeListId) return;
    try {
      await toggleItem(activeListId, itemId);
    } catch (error) {
      console.error('Error toggling item:', error);
    }
  };

  const handleGuestToggle = (itemId) => {
    const item = guestItems.find(i => i.id === itemId);
    if (item && updateGuestItem) {
      updateGuestItem(itemId, { isChecked: !item.isChecked });
    }
  };

  const handleCreateList = async () => {
    if (!listName.trim()) return;
    try {
      await createList(listName.trim());
      setShowCreateListModal(false);
      setListName('');
    } catch (error) {
      console.error('Error creating list:', error);
      alert('Failed to create list');
    }
  };

  const handleCreatePantry = async () => {
    if (!pantryName.trim()) return;
    try {
      await createPantry(pantryName.trim(), pantryDescription.trim());
      setShowCreatePantryModal(false);
      setPantryName('');
      setPantryDescription('');
    } catch (error) {
      console.error('Error creating pantry:', error);
      alert('Failed to create pantry');
    }
  };

  const openEditList = (list) => {
    setEditingItem(list);
    setListName(list.name);
    setShowEditModal(true);
  };

  const openEditPantry = (pantry) => {
    setEditingItem(pantry);
    setPantryName(pantry.name);
    setPantryDescription(pantry.description || '');
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    try {
      if (editingItem.items !== undefined) {
        await renameList(editingItem.id, listName.trim());
      } else {
        await renamePantry(editingItem.id, pantryName.trim());
      }
      setShowEditModal(false);
      setEditingItem(null);
      setListName('');
      setPantryName('');
      setPantryDescription('');
    } catch (error) {
      console.error('Error updating:', error);
      alert('Failed to update');
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Delete this ${type}? This cannot be undone.`)) return;
    try {
      if (type === 'list') {
        await deleteList(id);
        if (activeListId === id) setActiveListId(null);
      } else {
        await deletePantry(id);
        if (activePantryId === id) setActivePantryId(null);
      }
      setShowActionsMenu(null);
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      alert(`Failed to delete ${type}`);
    }
  };

  const handleArchive = async (id) => {
    try {
      await archiveList(id);
      if (activeListId === id) setActiveListId(null);
      setShowActionsMenu(null);
    } catch (error) {
      console.error('Error archiving list:', error);
      alert('Failed to archive list');
    }
  };

  const handleUnarchive = async (id) => {
    try {
      await unarchiveList(id);
    } catch (error) {
      console.error('Error unarchiving list:', error);
      alert('Failed to restore list');
    }
  };

  // Guest view
  if (!user) {
    const unchecked = guestItems.filter(i => !i.isChecked);
    const checked = guestItems.filter(i => i.isChecked);
    const guestProgress = guestItems.length > 0 ? Math.round((checked.length / guestItems.length) * 100) : 0;

    return (
      <div className="sticky top-4 space-y-sm" style={{ maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto' }}>
        <div className="rounded-large border border-text-tertiary/10 bg-surface overflow-hidden">
          {/* Header */}
          <div className="p-md border-b border-text-tertiary/10 flex items-center justify-between">
            <div className="flex items-center gap-sm">
              <ShoppingCart size={16} className="text-primary" />
              <h3 className="text-callout font-semibold text-primary">Shopping List</h3>
            </div>
            <span className="text-footnote text-text-tertiary">{guestItems.length} item{guestItems.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Progress bar */}
          {guestItems.length > 0 && (
            <div className="px-md pt-md pb-sm">
              <div className="flex items-center justify-between mb-xs">
                <span className="text-footnote text-text-secondary">{checked.length} of {guestItems.length} checked</span>
                <span className="text-footnote font-semibold text-primary">{guestProgress}%</span>
              </div>
              <div className="w-full h-1.5 bg-text-tertiary/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-300"
                  style={{ width: `${guestProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Items */}
          <div className="p-sm max-h-[400px] overflow-y-auto">
            {guestItems.length === 0 ? (
              <div className="text-center py-xl">
                <Package size={28} className="text-text-tertiary mx-auto mb-sm" />
                <p className="text-caption text-text-secondary">Add products to start</p>
              </div>
            ) : (
              <div className="space-y-xs">
                {unchecked.map(item => (
                  <ListItem key={item.id} item={item} onToggle={handleGuestToggle} />
                ))}
                {checked.length > 0 && unchecked.length > 0 && (
                  <div className="border-t border-text-tertiary/10 my-sm" />
                )}
                {checked.map(item => (
                  <ListItem key={item.id} item={item} onToggle={handleGuestToggle} />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-md border-t border-text-tertiary/10">
            <button
              onClick={() => navigate('/login')}
              className="w-full py-sm rounded-medium bg-primary/10 hover:bg-primary/20 text-primary text-caption font-semibold transition-all"
            >
              Sign in to sync your list
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated view
  return (
    <div className="sticky top-4 space-y-sm" style={{ maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto' }}>

      {/* Tab Toggle */}
      <div className="flex gap-xs p-xs bg-surface rounded-large border border-text-tertiary/10">
        <button
          onClick={() => setActiveTab('lists')}
          className={`flex-1 flex items-center justify-center gap-xs px-md py-sm rounded-medium text-caption font-semibold transition-all ${
            activeTab === 'lists' ? 'bg-primary text-white' : 'text-text-secondary hover:text-primary'
          }`}
        >
          <ShoppingCart size={13} />
          Lists
        </button>
        <button
          onClick={() => setActiveTab('pantries')}
          className={`flex-1 flex items-center justify-center gap-xs px-md py-sm rounded-medium text-caption font-semibold transition-all ${
            activeTab === 'pantries' ? 'bg-primary text-white' : 'text-text-secondary hover:text-primary'
          }`}
        >
          <Package size={13} />
          Pantries
        </button>
      </div>

      {/* ── LISTS TAB ── */}
      {activeTab === 'lists' && (
        <div className="rounded-large border border-text-tertiary/10 bg-surface overflow-hidden">

          {/* List selector header */}
          <div className="p-md border-b border-text-tertiary/10">
            <div className="flex items-center justify-between mb-sm">
              {groceryLists.length > 1 ? (
                <select
                  value={activeListId || ''}
                  onChange={(e) => setActiveListId(e.target.value)}
                  className="flex-1 bg-transparent text-callout font-semibold text-primary focus:outline-none cursor-pointer"
                >
                  {groceryLists.map(list => (
                    <option key={list.id} value={list.id}>{list.name}</option>
                  ))}
                </select>
              ) : (
                <h3 className="text-callout font-semibold text-primary truncate flex-1">
                  {activeList?.name || 'No List Selected'}
                </h3>
              )}

              {activeList && (
                <button
                  onClick={() => setShowActionsMenu(showActionsMenu === activeListId ? null : activeListId)}
                  className="w-7 h-7 rounded-full hover:bg-primary/10 flex items-center justify-center flex-shrink-0 ml-sm"
                >
                  <MoreVertical size={14} className="text-primary" />
                </button>
              )}
            </div>

            {/* Actions menu */}
            {showActionsMenu === activeListId && activeList && (
              <div className="mt-sm p-sm rounded-medium border border-text-tertiary/10 bg-background space-y-xs">
                <button onClick={() => { openEditList(activeList); setShowActionsMenu(null); }} className="w-full flex items-center gap-sm px-sm py-xs rounded-medium hover:bg-primary/10 text-left">
                  <Edit2 size={13} className="text-primary" />
                  <span className="text-caption text-primary">Rename</span>
                </button>
                <button onClick={() => handleArchive(activeListId)} className="w-full flex items-center gap-sm px-sm py-xs rounded-medium hover:bg-primary/10 text-left">
                  <Archive size={13} className="text-primary" />
                  <span className="text-caption text-primary">Archive</span>
                </button>
                <button onClick={() => handleDelete(activeListId, 'list')} className="w-full flex items-center gap-sm px-sm py-xs rounded-medium hover:bg-error/10 text-left">
                  <Trash2 size={13} className="text-error" />
                  <span className="text-caption text-error">Delete</span>
                </button>
              </div>
            )}

            {/* Progress */}
            {totalCount > 0 && (
              <div className="mt-sm">
                <div className="flex items-center justify-between mb-xs">
                  <span className="text-footnote text-text-secondary">{checkedCount} of {totalCount} checked</span>
                  <span className="text-footnote font-semibold text-primary">{progress}%</span>
                </div>
                <div className="w-full h-1.5 bg-text-tertiary/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Items list */}
          <div className="p-sm max-h-[360px] overflow-y-auto">
            {!activeList || totalCount === 0 ? (
              <div className="text-center py-xl">
                <Package size={28} className="text-text-tertiary mx-auto mb-sm" />
                <p className="text-caption text-text-secondary">No items</p>
              </div>
            ) : (
              <div className="space-y-xs">
                {activeList.items.filter(i => !i.isChecked).map((item, idx) => (
                  <ListItem key={item.id || idx} item={item} onToggle={handleToggleItem} />
                ))}
                {activeList.items.filter(i => i.isChecked).length > 0 &&
                  activeList.items.filter(i => !i.isChecked).length > 0 && (
                  <div className="border-t border-text-tertiary/10 my-xs" />
                )}
                {activeList.items.filter(i => i.isChecked).map((item, idx) => (
                  <ListItem key={item.id || idx} item={item} onToggle={handleToggleItem} />
                ))}
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="p-md border-t border-text-tertiary/10 flex gap-sm">
            <button
              onClick={() => setShowCreateListModal(true)}
              className="flex-1 flex items-center justify-center gap-xs py-sm rounded-medium bg-primary/5 hover:bg-primary/10 border border-primary/10 text-primary text-caption font-semibold transition-all"
            >
              <Plus size={13} />
              New List
            </button>
            <button
              onClick={() => navigate('/grocery-lists')}
              className="flex-1 flex items-center justify-center gap-xs py-sm rounded-medium hover:bg-surface border border-text-tertiary/20 text-text-secondary text-caption font-semibold transition-all"
            >
              <ChevronRight size={13} />
              All Lists
            </button>
          </div>
        </div>
      )}

      {/* ── ARCHIVED LISTS ── (only shown on lists tab) */}
      {activeTab === 'lists' && archivedLists.length > 0 && (
        <div className="rounded-large border border-text-tertiary/10 bg-surface overflow-hidden">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="w-full flex items-center justify-between px-md py-sm hover:bg-primary/5 transition-all"
          >
            <div className="flex items-center gap-sm">
              <Archive size={14} className="text-text-tertiary" />
              <span className="text-caption font-semibold text-text-secondary">Archived Lists</span>
              <span className="px-sm py-xs bg-text-tertiary/10 text-text-tertiary text-footnote rounded-full">{archivedLists.length}</span>
            </div>
            {showArchived ? <ChevronUp size={14} className="text-text-tertiary" /> : <ChevronDown size={14} className="text-text-tertiary" />}
          </button>

          {showArchived && (
            <div className="border-t border-text-tertiary/10">
              {archivedLists.map(list => (
                <div key={list.id} className="border-b border-text-tertiary/5 last:border-0">
                  {/* Archived list header */}
                  <button
                    onClick={() => setExpandedArchivedId(expandedArchivedId === list.id ? null : list.id)}
                    className="w-full flex items-center justify-between px-md py-sm hover:bg-primary/5 transition-all"
                  >
                    <div className="flex items-center gap-sm min-w-0">
                      <span className="text-caption font-semibold text-text-secondary truncate">{list.name}</span>
                      <span className="text-footnote text-text-tertiary flex-shrink-0">{list.items.length} items</span>
                    </div>
                    <div className="flex items-center gap-xs flex-shrink-0 ml-sm">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleUnarchive(list.id); }}
                        className="w-6 h-6 rounded-full hover:bg-primary/10 flex items-center justify-center"
                        title="Restore list"
                      >
                        <ArchiveRestore size={12} className="text-primary" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(list.id, 'list'); }}
                        className="w-6 h-6 rounded-full hover:bg-error/10 flex items-center justify-center"
                        title="Delete permanently"
                      >
                        <Trash2 size={12} className="text-error" />
                      </button>
                      {expandedArchivedId === list.id
                        ? <ChevronUp size={12} className="text-text-tertiary" />
                        : <ChevronDown size={12} className="text-text-tertiary" />
                      }
                    </div>
                  </button>

                  {/* Archived list items — read only, no toggle */}
                  {expandedArchivedId === list.id && (
                    <div className="px-sm pb-sm max-h-[200px] overflow-y-auto bg-surface/50">
                      {list.items.length === 0 ? (
                        <p className="text-caption text-text-tertiary text-center py-md">No items</p>
                      ) : (
                        <div className="space-y-xs">
                          {list.items.map((item, idx) => (
                            <ListItem key={item.id || idx} item={item} showToggle={false} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── PANTRIES TAB ── */}
      {activeTab === 'pantries' && (
        <div className="rounded-large border border-text-tertiary/10 bg-surface overflow-hidden">
          <div className="p-md border-b border-text-tertiary/10">
            <div className="flex items-center justify-between mb-sm">
              {pantries.length > 1 ? (
                <select
                  value={activePantryId || ''}
                  onChange={(e) => setActivePantryId(e.target.value)}
                  className="flex-1 bg-transparent text-callout font-semibold text-primary focus:outline-none cursor-pointer"
                >
                  {pantries.map(pantry => (
                    <option key={pantry.id} value={pantry.id}>{pantry.name}</option>
                  ))}
                </select>
              ) : (
                <h3 className="text-callout font-semibold text-primary truncate flex-1">
                  {activePantry?.name || 'No Pantry Selected'}
                </h3>
              )}

              {activePantry && (
                <button
                  onClick={() => setShowActionsMenu(showActionsMenu === activePantryId ? null : activePantryId)}
                  className="w-7 h-7 rounded-full hover:bg-primary/10 flex items-center justify-center flex-shrink-0 ml-sm"
                >
                  <MoreVertical size={14} className="text-primary" />
                </button>
              )}
            </div>

            {showActionsMenu === activePantryId && activePantry && (
              <div className="mt-sm p-sm rounded-medium border border-text-tertiary/10 bg-background space-y-xs">
                <button onClick={() => { openEditPantry(activePantry); setShowActionsMenu(null); }} className="w-full flex items-center gap-sm px-sm py-xs rounded-medium hover:bg-primary/10 text-left">
                  <Edit2 size={13} className="text-primary" />
                  <span className="text-caption text-primary">Edit Name</span>
                </button>
                <button onClick={() => handleDelete(activePantryId, 'pantry')} className="w-full flex items-center gap-sm px-sm py-xs rounded-medium hover:bg-error/10 text-left">
                  <Trash2 size={13} className="text-error" />
                  <span className="text-caption text-error">Delete</span>
                </button>
              </div>
            )}

            {activePantry && (
              <p className="text-caption text-text-secondary mt-xs">
                {activePantry.productIds.length} product{activePantry.productIds.length !== 1 ? 's' : ''}
                {activePantry.description ? ` · ${activePantry.description}` : ''}
              </p>
            )}
          </div>

          {!activePantry && (
            <div className="text-center py-xl px-md">
              <Package size={28} className="text-text-tertiary mx-auto mb-sm" />
              <p className="text-caption text-text-secondary">No pantry selected</p>
            </div>
          )}

          <div className="p-md border-t border-text-tertiary/10 flex gap-sm">
            <button
              onClick={() => setShowCreatePantryModal(true)}
              className="flex-1 flex items-center justify-center gap-xs py-sm rounded-medium bg-primary/5 hover:bg-primary/10 border border-primary/10 text-primary text-caption font-semibold transition-all"
            >
              <Plus size={13} />
              New Pantry
            </button>
            <button
              onClick={() => navigate('/pantry')}
              className="flex-1 flex items-center justify-center gap-xs py-sm rounded-medium hover:bg-surface border border-text-tertiary/20 text-text-secondary text-caption font-semibold transition-all"
            >
              <ChevronRight size={13} />
              All Pantries
            </button>
          </div>
        </div>
      )}

      {/* ── MODALS ── */}
      {showCreateListModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-lg">
          <div className="bg-background p-lg rounded-large border border-text-tertiary/20 max-w-md w-full">
            <div className="flex items-center justify-between mb-lg">
              <h3 className="text-title-3 font-rounded text-primary">New List</h3>
              <button onClick={() => setShowCreateListModal(false)} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center">
                <X size={20} className="text-text-tertiary" />
              </button>
            </div>
            <div className="space-y-md">
              <input
                type="text"
                placeholder="List name"
                value={listName}
                onChange={(e) => setListName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateList()}
                className="w-full px-md py-sm bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body"
                autoFocus
              />
              <div className="flex gap-md justify-end">
                <Button variant="outline" onClick={() => setShowCreateListModal(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleCreateList} disabled={!listName.trim()}>Create</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreatePantryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-lg">
          <div className="bg-background p-lg rounded-large border border-text-tertiary/20 max-w-md w-full">
            <div className="flex items-center justify-between mb-lg">
              <h3 className="text-title-3 font-rounded text-primary">New Pantry</h3>
              <button onClick={() => setShowCreatePantryModal(false)} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center">
                <X size={20} className="text-text-tertiary" />
              </button>
            </div>
            <div className="space-y-md">
              <input
                type="text"
                placeholder="Pantry name"
                value={pantryName}
                onChange={(e) => setPantryName(e.target.value)}
                className="w-full px-md py-sm bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body"
                autoFocus
              />
              <textarea
                placeholder="Description (optional)"
                value={pantryDescription}
                onChange={(e) => setPantryDescription(e.target.value)}
                className="w-full px-md py-sm bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body resize-none"
                rows={2}
              />
              <div className="flex gap-md justify-end">
                <Button variant="outline" onClick={() => setShowCreatePantryModal(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleCreatePantry} disabled={!pantryName.trim()}>Create</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-lg">
          <div className="bg-background p-lg rounded-large border border-text-tertiary/20 max-w-md w-full">
            <div className="flex items-center justify-between mb-lg">
              <h3 className="text-title-3 font-rounded text-primary">
                Edit {editingItem.items !== undefined ? 'List' : 'Pantry'}
              </h3>
              <button onClick={() => setShowEditModal(false)} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center">
                <X size={20} className="text-text-tertiary" />
              </button>
            </div>
            <div className="space-y-md">
              <input
                type="text"
                placeholder="Name"
                value={editingItem.items !== undefined ? listName : pantryName}
                onChange={(e) => editingItem.items !== undefined ? setListName(e.target.value) : setPantryName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                className="w-full px-md py-sm bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body"
                autoFocus
              />
              <div className="flex gap-md justify-end">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleSaveEdit}>Save</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsSidebar;
